import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  BookOpen, Check, CheckCircle2, ChevronDown, Clock, FileText, Loader2, Search,
  ShieldCheck, Upload, X, XCircle,
} from 'lucide-react';
import { API_BASE, authedFetch } from '@/lib/api';
import {
  BOARD_OPTIONS, CLASS_OPTIONS, SYLLABUS_NAMING_HELP, SYLLABUS_RIGHTS_COPY,
  fetchMySyllabus, searchSchools, sendChapterFeedback, suggestSchool, uploadMySyllabus,
} from '@/lib/studentSyllabus';
import { useAuth } from '@/contexts/AuthContext';

const STATUS = {
  approved: { label: 'Approved', icon: CheckCircle2, cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  pending_verification: { label: 'With our team', icon: Clock, cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  rejected: { label: 'Not accepted', icon: XCircle, cls: 'bg-rose-50 text-rose-700 border-rose-200' },
};

/** One chapter row with its two verdict buttons. */
function ChapterRow({ subject, row, onVerdict }) {
  const [busy, setBusy] = useState('');
  const verdict = row.verdict || '';
  const press = async (next) => {
    setBusy(next);
    try {
      const res = await sendChapterFeedback({
        subject, chapter: row.name, verdict: verdict === next ? 'clear' : next,
      });
      onVerdict(subject, row.name, verdict === next ? '' : next);
      if (res.message) toast.success(res.message);
    } catch (e) { toast.error(e?.message || 'That did not save'); }
    setBusy('');
  };
  const slug = row.name.replace(/[^a-z0-9]+/gi, '-').toLowerCase();

  return (
    <li className="flex items-start gap-2 py-1" data-testid={`chapter-row-${slug}`}>
      <span className={`mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0 ${
        verdict === 'not_applicable' ? 'bg-rose-300' : verdict === 'applicable' ? 'bg-emerald-400' : 'bg-indigo-300'}`} />
      <span className={`font-body text-[12px] flex-1 min-w-0 ${
        verdict === 'not_applicable' ? 'text-slate-400 line-through' : 'text-slate-600'}`}>
        {row.name}
        {verdict === 'not_applicable' && (
          <span className="ml-1.5 font-heading text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 whitespace-nowrap"
                data-testid={`chapter-under-review-${slug}`}>Under review</span>
        )}
      </span>
      <span className="flex items-center gap-1 flex-shrink-0">
        <button onClick={() => press('applicable')} disabled={!!busy} data-testid={`chapter-yes-${slug}`}
          title="Applicable — we have this chapter"
          className={`w-6 h-6 rounded-md border flex items-center justify-center transition-colors ${
            verdict === 'applicable' ? 'bg-emerald-500 border-emerald-500 text-white'
              : 'border-slate-200 text-slate-400 hover:border-emerald-400 hover:text-emerald-600'}`}>
          {busy === 'applicable' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
        </button>
        <button onClick={() => press('not_applicable')} disabled={!!busy} data-testid={`chapter-no-${slug}`}
          title="Not applicable — we do not have this chapter"
          className={`h-6 px-1.5 rounded-md border font-heading text-[10px] flex items-center justify-center transition-colors ${
            verdict === 'not_applicable' ? 'bg-rose-500 border-rose-500 text-white'
              : 'border-slate-200 text-slate-400 hover:border-rose-400 hover:text-rose-600'}`}>
          {busy === 'not_applicable' ? <Loader2 className="w-3 h-3 animate-spin" /> : 'NA'}
        </button>
      </span>
    </li>
  );
}

/** School field: type to search the list, or add your own for our team to check. */
function SchoolField({ value, onChange }) {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const box = useRef(null);

  // A child who taps Change must be able to change their mind: Escape, or a tap
  // anywhere outside, puts the previous school back.
  useEffect(() => {
    if (!open) return;
    const close = () => { setOpen(false); setQ(''); };
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    const onDown = (e) => { if (box.current && !box.current.contains(e.target)) close(); };
    window.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => { window.removeEventListener('keydown', onKey); document.removeEventListener('mousedown', onDown); };
  }, [open]);

  useEffect(() => {
    if (!open || q.trim().length < 2) { setRows([]); return; }
    const id = setTimeout(() => {
      searchSchools(q.trim()).then(d => setRows(d.schools || [])).catch(() => setRows([]));
    }, 250);
    return () => clearTimeout(id);
  }, [q, open]);

  const addMine = async () => {
    setAdding(true);
    try {
      const res = await suggestSchool({ name: q.trim() });
      onChange(res.school);
      setOpen(false); setQ('');
      toast.success(res.message);
    } catch (e) { toast.error(e?.message || 'That name was not accepted'); }
    setAdding(false);
  };

  return (
    <div className="relative" ref={box}>
      <span className="font-heading text-[11px] uppercase tracking-widest text-slate-400">School</span>
      {value && !open ? (
        <div className="mt-1 flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2">
          <span className="font-body text-[13px] text-slate-700 flex-1 min-w-0 truncate" data-testid="syllabus-school-value">{value}</span>
          <button onClick={() => { setOpen(true); setQ(''); }} data-testid="syllabus-school-change"
            className="font-heading text-[11px] text-indigo-600 hover:underline">Change</button>
        </div>
      ) : (
        <>
          <div className="mt-1 flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2">
            <Search className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
            <input value={q} autoFocus={open} onFocus={() => setOpen(true)} data-testid="syllabus-school-search"
              onKeyDown={e => { if (e.key === 'Escape') { setOpen(false); setQ(''); } }}
              onChange={e => setQ(e.target.value)} placeholder="Start typing your school's name"
              className="flex-1 min-w-0 font-body text-[13px] text-slate-700 outline-none" />
            {value && (
              <button onClick={() => { setOpen(false); setQ(''); }} data-testid="syllabus-school-keep"
                className="font-heading text-[11px] text-slate-400 hover:text-slate-600 flex-shrink-0">Keep {value.slice(0, 18)}</button>
            )}
          </div>
          {open && q.trim().length >= 2 && (
            <div className="absolute z-20 left-0 right-0 mt-1 max-h-56 overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-lg"
                 data-testid="syllabus-school-results">
              {rows.map(r => (
                <button key={`${r.name}-${r.city}`} onClick={() => { onChange(r.name); setOpen(false); }}
                  className="w-full text-left px-3 py-2 hover:bg-slate-50 font-body text-[12px] text-slate-700">
                  {r.name}
                  {(r.city || r.state) && <span className="text-slate-400"> · {[r.city, r.state].filter(Boolean).join(', ')}</span>}
                  {r.pending && <span className="ml-1.5 text-[10px] text-amber-700">(awaiting our check)</span>}
                </button>
              ))}
              <button onClick={addMine} disabled={adding} data-testid="syllabus-school-add"
                className="w-full text-left px-3 py-2 border-t border-slate-100 hover:bg-indigo-50 font-heading text-[12px] text-indigo-700">
                {adding ? 'Adding…' : `My school isn't listed — add "${q.trim()}"`}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/**
 * Syllabus — the student's own view of what we have mapped for their class, the
 * place they tell us a chapter isn't in their book, and the place they file a
 * missing chapter from any book they own.
 */
export default function SyllabusPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [open, setOpen] = useState('');
  const [form, setForm] = useState({ subject: '', topic_name: '', book_name: '', rights: false, board: '', class_level: '', school: '' });
  const [contents, setContents] = useState(null);
  const [searchParams] = useSearchParams();
  const uploadCardRef = useRef(null);
  const contentsRef = useRef(null);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try { setData(await fetchMySyllabus()); }
    catch (e) { setError(e?.message || 'We could not load your syllabus just now'); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    setForm(f => ({
      ...f,
      board: f.board || data?.board || user?.board || '',
      class_level: f.class_level || String(data?.class_level || user?.class_level || ''),
      school: f.school || user?.school || '',
    }));
  }, [data, user]);

  // Snap & Solve sends the student here with the subject it read off the scan.
  useEffect(() => {
    const subject = searchParams.get('subject');
    if (subject) setForm(f => (f.subject ? f : { ...f, subject }));
    if (searchParams.get('upload')) {
      const t = setTimeout(() => uploadCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 500);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const clearFile = () => { setFile(null); if (fileRef.current) fileRef.current.value = ''; };

  const submit = async () => {
    if (!form.subject.trim() || !form.topic_name.trim()) { toast.error('Add the subject and the chapter name'); return; }
    if (!file) { toast.error('Choose the pages you want to upload'); return; }
    if (!form.rights) { toast.error('Please confirm the book is yours before uploading'); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await authedFetch(`${API_BASE}/syllabus/my/file`, { method: 'POST', body: fd });
      const stored = await res.json();
      if (!res.ok) throw new Error(stored.detail || 'The upload did not go through');
      let contentsUrl = '';
      if (contents) {
        const cfd = new FormData();
        cfd.append('file', contents);
        const cres = await authedFetch(`${API_BASE}/syllabus/my/file`, { method: 'POST', body: cfd });
        const cstored = await cres.json();
        if (!cres.ok) throw new Error(cstored.detail || 'The contents page did not go through');
        contentsUrl = cstored.file_url;
      }
      const saved = await uploadMySyllabus({
        subject: form.subject.trim(),
        topic_name: form.topic_name.trim(),
        book_name: form.book_name.trim(),
        contents_screenshot_url: contentsUrl,
        pdf_url: stored.file_url,
        rights_declared: true,
        board: form.board,
        class_level: form.class_level,
        school: form.school,
      });
      toast.success(saved.message || 'Sent for review');
      setForm(f => ({ ...f, subject: '', topic_name: '', book_name: '', rights: false }));
      clearFile();
      setContents(null); if (contentsRef.current) contentsRef.current.value = '';
      load();
    } catch (e) { toast.error(e?.message || 'The upload did not go through'); }
    setBusy(false);
  };

  const onVerdict = (subject, chapter, verdict) => setData(d => !d ? d : ({
    ...d,
    subjects: d.subjects.map(s => s.subject !== subject ? s : ({
      ...s, chapter_rows: (s.chapter_rows || []).map(r => r.name === chapter ? { ...r, verdict } : r),
    })),
  }));

  const subjects = data?.subjects || [];
  const filed = data?.submissions || [];
  const rowsOf = (s) => s.chapter_rows || (s.chapters || []).map(name => ({ name, verdict: '' }));

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-6" data-testid="syllabus-page">
      <div>
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900">Syllabus</h1>
        <p className="font-body text-sm text-slate-500 mt-1" data-testid="syllabus-scope">
          {data ? `${data.board} · Class ${data.class_level}` : 'Loading your class…'} — this is your own class only.
        </p>
      </div>

      {error && (
        <div className="rounded-2xl border border-rose-200 bg-rose-50/70 p-4" data-testid="syllabus-page-error">
          <p className="font-body text-sm text-rose-700">{error}</p>
        </div>
      )}

      {data && (
        data.has_curriculum ? (
          <div className="space-y-2" data-testid="syllabus-mapped">
            <p className="font-heading text-xs uppercase tracking-widest text-slate-400">Mapped for your class</p>
            <div className="rounded-2xl border border-indigo-100 bg-indigo-50/60 px-4 py-3" data-testid="chapter-verdict-help">
              <p className="font-heading text-[12px] text-slate-800">Does your book match this list?</p>
              <p className="font-body text-[11.5px] text-slate-600 mt-1 leading-relaxed">
                Open a subject and tell us about each chapter.
                <span className="inline-flex items-center gap-1 mx-1 align-[-2px]">
                  <span className="w-4 h-4 rounded bg-emerald-500 text-white inline-flex items-center justify-center"><Check className="w-3 h-3" /></span>
                  <strong>APPLICABLE</strong>
                </span>
                means <em>we have this chapter</em>.
                <span className="inline-flex items-center gap-1 mx-1 align-[-2px]">
                  <span className="px-1 rounded bg-rose-500 text-white font-heading text-[9px]">NA</span>
                  <strong>NOT APPLICABLE</strong>
                </span>
                means <em>we do not have this chapter</em>.
              </p>
              <p className="font-body text-[11.5px] text-slate-500 mt-1.5 leading-relaxed">
                A chapter you mark NA is shown as <strong>Under review</strong>, not deleted straight away:
                <strong> three students</strong> from your board and class must say the same before it leaves
                the list. That protects you from losing a chapter you actually have because one classmate
                tapped the wrong button.
              </p>
            </div>
            {subjects.filter(s => rowsOf(s).length).map(s => (
              <div key={s.subject} className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
                <button onClick={() => setOpen(o => (o === s.subject ? '' : s.subject))}
                  data-testid={`syllabus-subject-${s.subject.replace(/\s/g, '-').toLowerCase()}`}
                  className="w-full flex items-center gap-2.5 px-4 py-3 text-left hover:bg-slate-50 transition-colors">
                  <BookOpen className="w-4 h-4 text-indigo-500 flex-shrink-0" />
                  <span className="font-heading text-[13px] text-slate-800 flex-1 min-w-0 truncate">{s.subject}</span>
                  <span className="font-body text-[11px] text-slate-400">{rowsOf(s).length} chapters</span>
                  <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${open === s.subject ? 'rotate-180' : ''}`} />
                </button>
                {open === s.subject && (
                  <ul className="px-4 pb-3 grid sm:grid-cols-2 gap-x-6" data-testid={`syllabus-chapters-${s.subject.replace(/\s/g, '-').toLowerCase()}`}>
                    {rowsOf(s).map(row => (
                      <ChapterRow key={row.name} subject={s.subject} row={row} onVerdict={onVerdict} />
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="rounded-3xl border border-amber-200 bg-amber-50/70 p-4 sm:p-5" data-testid="syllabus-missing">
            <p className="font-heading text-sm font-bold text-amber-900">
              We don't have the {data.board} Class {data.class_level} syllabus yet
            </p>
            <p className="font-body text-[13px] text-amber-900/90 mt-1">
              Nothing is lost — upload your own book below, one chapter at a time. Our team reviews it and
              builds your material, and you earn Veda Coins for every chapter we approve.
            </p>
          </div>
        )
      )}

      {/* individual upload */}
      <div className="rounded-3xl border border-slate-200 bg-white p-4 sm:p-5 space-y-4" data-testid="syllabus-upload-card">
        <div>
          <p className="font-heading text-sm font-bold text-slate-900">Add a subject or chapter</p>
          <p className="font-body text-[12px] text-slate-600 mt-1 leading-relaxed" data-testid="syllabus-naming-help">
            <strong className="font-heading text-slate-900">{SYLLABUS_NAMING_HELP.rule}</strong> — and please
            rename the file before you choose it, exactly like this:
            <br />
            <span className="inline-block mt-1 rounded-lg bg-slate-900 text-white font-mono text-[11px] px-2 py-1">
              {data?.naming_example || 'CBSE_Class VIII_Mathematics_Prime Numbers.pdf'}
            </span>
            <br />
            <strong className="font-heading text-[11px] uppercase tracking-wide text-slate-900">
              BOARD_CLASS_SUBJECT_CHAPTER.PDF
            </strong>{' '}
            — {SYLLABUS_NAMING_HELP.why}
          </p>
        </div>

        {/* which book is this from? */}
        <div className="grid sm:grid-cols-2 gap-3">
          <label className="block">
            <span className="font-heading text-[11px] uppercase tracking-widest text-slate-400">Education board</span>
            <select value={form.board} onChange={e => setForm(f => ({ ...f, board: e.target.value }))}
              data-testid="syllabus-board-select"
              className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 font-body text-[13px] text-slate-700 bg-white">
              {!BOARD_OPTIONS.includes(form.board) && form.board && <option value={form.board}>{form.board}</option>}
              {BOARD_OPTIONS.map(b => <option key={b} value={b}>{b}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="font-heading text-[11px] uppercase tracking-widest text-slate-400">Class of the book</span>
            <select value={form.class_level} onChange={e => setForm(f => ({ ...f, class_level: e.target.value }))}
              data-testid="syllabus-class-select"
              className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 font-body text-[13px] text-slate-700 bg-white">
              {CLASS_OPTIONS.map(c => <option key={c} value={c}>Class {c}</option>)}
            </select>
          </label>
        </div>
        <p className="font-body text-[11px] text-slate-500 -mt-2">
          Uploading from an older book? Change the class — a Class 7 chapter from last year's book is welcome.
        </p>

        <span ref={uploadCardRef} />
        <SchoolField value={form.school} onChange={v => setForm(f => ({ ...f, school: v }))} />

        <div className="grid sm:grid-cols-2 gap-3">
          <label className="block">
            <span className="font-heading text-[11px] uppercase tracking-widest text-slate-400">Subject</span>
            <input value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))}
              list="syllabus-subject-options" placeholder="e.g. Mathematics" maxLength={120}
              data-testid="syllabus-subject-input"
              className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 font-body text-[13px] text-slate-700" />
            <datalist id="syllabus-subject-options">
              {subjects.map(s => <option key={s.subject} value={s.subject} />)}
            </datalist>
          </label>
          <label className="block">
            <span className="font-heading text-[11px] uppercase tracking-widest text-slate-400">Chapter name</span>
            <input value={form.topic_name} onChange={e => setForm(f => ({ ...f, topic_name: e.target.value }))}
              placeholder="e.g. Prime Numbers" maxLength={200} data-testid="syllabus-chapter-input"
              className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 font-body text-[13px] text-slate-700" />
          </label>
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <label className="block">
            <span className="font-heading text-[11px] uppercase tracking-widest text-slate-400">Book name</span>
            <input value={form.book_name} onChange={e => setForm(f => ({ ...f, book_name: e.target.value }))}
              placeholder="e.g. NCERT First Flight" maxLength={160} data-testid="syllabus-book-input"
              className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 font-body text-[13px] text-slate-700" />
            <span className="font-body text-[11px] text-slate-400">The name printed on the cover — not “Maths book”.</span>
          </label>
          <label className="block">
            <span className="font-heading text-[11px] uppercase tracking-widest text-slate-400">Contents page (optional)</span>
            <input ref={contentsRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" data-testid="syllabus-contents-picker"
              onChange={e => setContents(e.target.files?.[0] || null)}
              className="mt-1 w-full rounded-xl border border-dashed border-slate-300 px-3 py-2 font-body text-[12px] text-slate-600" />
            <span className="font-body text-[11px] text-slate-400">
              A photo of the book's contents page tells us where this chapter sits.
            </span>
          </label>
        </div>

        <div className="block">
          <span className="font-heading text-[11px] uppercase tracking-widest text-slate-400">Chapter pages</span>
          <div className="mt-1 flex items-center gap-2">
            <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" data-testid="syllabus-file-picker"
              onChange={e => setFile(e.target.files?.[0] || null)}
              className="flex-1 min-w-0 rounded-xl border border-dashed border-slate-300 px-3 py-2 font-body text-[12px] text-slate-600" />
            {file && (
              <button onClick={clearFile} data-testid="syllabus-file-cancel" title="Remove this file and choose another"
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-slate-200 font-heading text-[11px] text-slate-600 hover:border-rose-300 hover:text-rose-600 flex-shrink-0">
                <X className="w-3.5 h-3.5" /> Cancel
              </button>
            )}
          </div>
          <span className="font-body text-[11px] text-slate-400">
            PDF or a clear photo · up to 25 MB{file ? ` · chosen: ${file.name}` : ''}
          </span>
        </div>

        <label className="flex items-start gap-2" data-testid="syllabus-rights-label">
          <input type="checkbox" checked={form.rights} data-testid="syllabus-rights-checkbox"
            onChange={e => setForm(f => ({ ...f, rights: e.target.checked }))} className="mt-1" />
          <span className="font-body text-[12px] text-slate-600 leading-relaxed">
            <ShieldCheck className="w-3.5 h-3.5 inline-block mr-1 text-emerald-600 align-[-2px]" />
            You may upload pages <strong>ONLY from a book you or your family own and have paid for</strong> —
            never a file downloaded from the internet. A book being available online does not make it free to
            share, and <strong>our review is not permission to redistribute it</strong>: we use your pages to
            build your study material, nothing else.
          </span>
        </label>

        <button onClick={submit} disabled={busy} data-testid="syllabus-submit"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-heading text-sm disabled:opacity-50">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          Send for review
        </button>
      </div>

      {/* what the student has filed */}
      {filed.length > 0 && (
        <div className="space-y-2" data-testid="syllabus-submissions">
          <p className="font-heading text-xs uppercase tracking-widest text-slate-400">Your uploads</p>
          {filed.map(row => {
            const meta = STATUS[row.status] || STATUS.pending_verification;
            const Icon = meta.icon;
            return (
              <div key={row.submission_id} data-testid={`syllabus-submission-${row.submission_id}`}
                className="rounded-2xl border border-slate-200 bg-white px-4 py-3 flex items-center gap-3">
                <FileText className="w-4 h-4 text-slate-400 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="font-heading text-[13px] text-slate-800 truncate">
                    {row.file_name || `${row.subject} · ${row.topic_name}`}
                  </p>
                  {row.admin_notes && <p className="font-body text-[11px] text-slate-500 truncate">{row.admin_notes}</p>}
                </div>
                <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-[11px] font-heading flex-shrink-0 ${meta.cls}`}>
                  <Icon className="w-3 h-3" /> {meta.label}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
